"use strict";

import { keys, values } from 'underscore';
import FilterModel from './FilterModel';

/**
 * The backing data for a UI facet component.
 *
 * @param id The id of the facet. Likely named after a property from the search result items.
 * @param label The displayed label for the facet (e.g. "Subject Area").
 * @param valueFinderMethod A method which when called will calculate the value(s) for a particular facet as it relates
 * to a search result item. Can return a string or an array of strings.
 * @param filterSortMethod A comparator method to order the filters.
 * @param component The component which will be used for filter widgets. Valid values are: checkbox, button, slider.
 * @param analyticsKey The key to use for analytics. This is mostly for backward compatibility with previously used keys.
 * @param helpContent Text or HTML to be used in a popover for help about the current facet.
 *                     If you don't specify, one will be generated from the id.
 */
const FacetModel = function (id, label, valueFinderMethod, filterSortMethod, component, analyticsKey, helpContent, cssClasses) {
    this.id = id;
    this.label = label;
    this.valueFinderMethod = valueFinderMethod;
    this.filterSortMethod = filterSortMethod;
    this.filterModels = {};
    this.allFiltersActive = true;
    this.noValueString = "None";
    // The component which will be used to render filters. Default to checkbox
    if ( component ) {
        this.component = component;
    } else {
        this.component = "checkbox";
    }
    this.analyticsKey = analyticsKey;
    this.helpContent = helpContent;
    if ( cssClasses ) {
        this.cssClasses = cssClasses;
    } else {
        this.cssClasses = "";
    }
};

FacetModel.prototype.getId = function() {
    return this.id;
};

FacetModel.prototype.getLabel = function() {
    return this.label;
};

FacetModel.prototype.getComponent = function() {
    return this.component;
};

FacetModel.prototype.getAnalyticsKey = function() {
    return this.analyticsKey;
};

FacetModel.prototype.getHelpContent = function() {
    return this.helpContent;
};

FacetModel.prototype.getCssClasses = function() {
    return this.cssClasses;
};

/*
 * Returns an array of all filter ids.
 */
FacetModel.prototype.getFilterIds = function() {
    return keys(this.filterModels);
};

FacetModel.prototype.isActive = function() {
    return ( this.filterModels.length > 1 );
};

FacetModel.prototype.valueFinder = function(object) {
    return this.valueFinderMethod(object);
};

/**
 * Get filters as an array sorted by key.
 */
FacetModel.prototype.getFilterModels = function() {
    let filterArray = values(this.filterModels);

    let sortMethod = null;
    // If a sort method is provided then use that.
    if ( this.filterSortMethod ) {
        sortMethod = this.filterSortMethod;
    } else {
        //  Otherwise, default to an alpha-numberic sort on the filter key.

        let noValueString = this.noValueString;

        sortMethod = function(a, b) {
            if ( a.key === noValueString ) return 1;
            if ( b.key === noValueString ) return -1;

            if ( a.key < b.key ) {
                return -1;
            }
            if ( a.key > b.key ) {
                return 1;
            }
            // Still equal (this shouldn't happen)
            return 0;
        };
    }

    filterArray.sort(sortMethod);
    return filterArray;
};

FacetModel.prototype.getFilter = function(id) {
    return this.filterModels[id];
};

/**
 * Gets the value of the allFiltersActive flag.
 */
FacetModel.prototype.isAllFiltersActive = function() {
    return this.allFiltersActive;
};

/**
 * Returns true if more than one of the contained filters are actionable.
 * Otherwise, false.
 */
FacetModel.prototype.isActionable = function() {
    let actionableCount = 0;
    for ( let filterKey in this.filterModels ) {
        let filter = this.filterModels[filterKey];
        if ( filter.isActionable() ) {
            actionableCount++;
        }
        if ( actionableCount > 1 ) {
            break;
        }
    }
    return ( actionableCount > 1 );
};

/**
 * Sets the allFiltersActive flag. Also, turns filter the selected flags off if the new state is 'true'.
 *
 * @param active True turns the flag on. False turns it off.
 */
FacetModel.prototype.setAllFiltersActive = function(active) {
    //  Don't do anything if the state isn't changing
    if ( this.allFiltersActive === active ) {
        return;
    }

    this.allFiltersActive = active;
    //  If the flag is turned on then all of the filters need to be turned off.
    if ( active ) {
        for ( let filterKey in this.filterModels ) {
            let filter = this.filterModels[filterKey];
            filter.setSelected(false);
        }
    }
};

/**
 * Resets match counts for each filter.
 */
FacetModel.prototype.resetFilterCounts = function() {
    for ( let filterKey in this.filterModels ) {
        let filter = this.filterModels[filterKey];
        filter.resetMatchCount();
    }
};

/**
 * Removes filters.
 */
FacetModel.prototype.removeFilters = function() {
    this.filterModels = {};
};

/**
 * Inspects a search result item and either creates a new filter model or increments match count.
 *
 * Returns a map containing the filter ids that were created or matched.
 */
FacetModel.prototype.process = function(object) {
    let filterIds = {};
    // Call the getValues() method to read the value from the object
    let values = this.getValues(object);
    /*
     * Look for a filter for each value. If it exists then increment it. Otherwise,
     * create it.
     */
    let matchingFilters = this.getMatchingFilters(values);
    for ( let x = 0, xLen = values.length; x < xLen; x++ ) {
        let value = values[x];
        if ( ! value ) {
            value = this.noValueString;
        }
        let filter = matchingFilters[value];
        let filterId = null;
        if ( filter ) {
            filterId = filter.id;
            filter.incrementMatchCount();
        } else {
            //  If the filter doesn't already exist then create it.
            filterId = this.id + "__" + value;
            if ( ! this.filterModels[filterId] ) {
                this.filterModels[filterId] = new FilterModel(filterId, value);
            }

        }
        // The value of this map isn't used.
        filterIds[filterId] = null;
    }
    return ( filterIds );
};

/**
 * Reads the values from the given search result item.
 */
FacetModel.prototype.getValues = function(object) {
    let values = [];
    let v = this.valueFinder(object);
    let isArray = Array.isArray(v);

    //  Values can be a string or an array of stings.
    if ( ! isArray ) {
        //  Add the string to the array.
        values.push(v);
    } else {
        // Add all of the values if it's an array.
        values = v;
    }
    return values;
};

/**
 * Gets selected/on filters where the filter key matches the given values. For example,
 * the filter keys for a course subject ares filter might be ENGL, MATH. If the value
 * passed in is ["ENGL"] then the ENGL filter would be returned.
 *
 * Returns a map where the keys are the strings in the values array and the value
 * is the filter model that matches the given value. Only returns filters that
 * are selected.
 *
 * If the allFiltersActive flag is on the isSelected state of the filter is ignored.
 */
FacetModel.prototype.getMatchingFilters = function(values) {
    let filters = {};
    for ( let x = 0, xLen = values.length; x < xLen; x++ ) {
        if ( ! values[x] ) {
            values[x] = this.noValueString;
        }
        let value = values[x];

        for ( let filterKey in this.filterModels ) {
            let filter = this.filterModels[filterKey]
            if ( ! filter.isSelected() && ! this.isAllFiltersActive() ) {
                continue;
            }
            if ( value === filter.getKey() ) {
                filters[value] = filter;
                break;
            }
        }
    }
    return filters;
};

export default FacetModel;